import { db } from './micuDatabase';
import { calculateTimeRemaining } from './calculations';

let intervalId = null;
let activeAlarms = []; // { medicationId, drugName, minutesRemaining }
let listeners = [];

// Web Audio API tone generator
function playAlarmTone() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;

    // Generate alternating frequency medical alert pattern
    const pattern = [
      { freq: 880, start: 0, dur: 0.15 },
      { freq: 660, start: 0.2, dur: 0.15 },
      { freq: 880, start: 0.4, dur: 0.15 },
      { freq: 660, start: 0.6, dur: 0.15 },
      { freq: 880, start: 0.9, dur: 0.15 },
      { freq: 660, start: 1.1, dur: 0.15 },
    ];

    for (const tone of pattern) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = tone.freq;
      gain.gain.setValueAtTime(0.3, now + tone.start);
      gain.gain.exponentialRampToValueAtTime(0.01, now + tone.start + tone.dur);
      osc.start(now + tone.start);
      osc.stop(now + tone.start + tone.dur + 0.05);
    }

    // Close context after tones complete
    setTimeout(() => ctx.close(), 2000);
  } catch {
    // Audio not available
  }
}

function triggerHaptic() {
  try {
    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200, 100, 200]);
    }
  } catch {
    // Haptic not available
  }
}

async function checkAlarms() {
  try {
    const allMeds = await db.medications.toArray();
    const newAlarms = [];

    for (const med of allMeds) {
      if (!med.vtbi || !med.rate || med.rate <= 0) continue;

      // Calculate actual time remaining based on startedAt
      let minutesRemaining;
      if (med.startedAt) {
        const elapsed = (Date.now() - new Date(med.startedAt).getTime()) / 60000; // minutes
        const totalMinutes = (med.vtbi / med.rate) * 60;
        minutesRemaining = totalMinutes - elapsed;
      } else {
        minutesRemaining = calculateTimeRemaining(med.vtbi, med.rate);
      }

      if (minutesRemaining == null || minutesRemaining < 0) minutesRemaining = 0;

      const threshold = med.alarmMinutes || 15;

      if (minutesRemaining <= threshold) {
        // Check if this alarm is already active
        const alreadyActive = activeAlarms.some(a => a.medicationId === med.id);

        newAlarms.push({
          medicationId: med.id,
          drugName: med.drugName,
          minutesRemaining: Math.round(minutesRemaining),
          caseId: med.caseId,
        });

        if (!alreadyActive) {
          // New alarm trigger
          playAlarmTone();
          triggerHaptic();
        }
      }
    }

    activeAlarms = newAlarms;
    notifyListeners();
  } catch {
    // IndexedDB may not be available
  }
}

function notifyListeners() {
  for (const listener of listeners) {
    try { listener([...activeAlarms]); } catch { /* noop */ }
  }
}

export function startAlarmManager() {
  if (intervalId) return;
  intervalId = setInterval(checkAlarms, 30000);
  // Immediate first check
  checkAlarms();
}

export function stopAlarmManager() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

export function subscribeToAlarms(callback) {
  listeners.push(callback);
  // Immediately send current state
  callback([...activeAlarms]);
  return () => {
    listeners = listeners.filter(l => l !== callback);
  };
}

export function dismissAlarm(medicationId) {
  activeAlarms = activeAlarms.filter(a => a.medicationId !== medicationId);
  notifyListeners();
}

export function getActiveAlarms() {
  return [...activeAlarms];
}
